/**
 * Tools Data
 *
 * Centralized definition of all tools available on The Art of CTO.
 * Used by:
 * - /tools page for display
 * - /api/secure/tools/metadata for PosterBot and other integrations
 */

export interface Tool {
  href: string;
  title: string;
  description: string;
  category: string;
  tags: { label: string; highlight?: boolean }[];
  badge?: 'FLAGSHIP' | 'NEW' | 'PREMIUM';
  /** Topics this tool is relevant for - used by AI to determine when to reference */
  relevantTopics?: string[];
}

export interface ToolCategory {
  id: string;
  title: string;
  icon: string;
  description: string;
  tools: Tool[];
}

export const toolCategories: ToolCategory[] = [
  {
    id: 'flagship',
    title: 'Flagship Tools',
    icon: '🚀',
    description: 'Enterprise-grade tools with database persistence and advanced features.',
    tools: [
      {
        href: '/command-center',
        title: 'Command Center',
        description: 'Track and assess your technology landscape, manage tech debt, incidents, risks, SLOs, migrations, and team capacity.',
        category: 'flagship',
        tags: [
          { label: 'Dashboard', highlight: true },
          { label: 'Database-Backed', highlight: true },
        ],
        badge: 'FLAGSHIP',
        relevantTopics: ['tech portfolio management', 'architecture governance', 'technology assessments', 'incident management', 'operational visibility', 'tech debt', 'SLOs', 'risk management', 'team capacity'],
      },
      {
        href: '/splitcause',
        title: 'Split Cause (RCA)',
        description: 'Graph-based root cause analysis with Bayesian inference',
        category: 'flagship',
        tags: [
          { label: 'Interactive Graph', highlight: true },
          { label: 'Database-Backed', highlight: true },
        ],
        badge: 'FLAGSHIP',
        relevantTopics: ['root cause analysis', 'incident investigation', 'debugging', 'system failures', 'outage analysis'],
      },
      {
        href: '/tools/archimate',
        title: 'ArchiMate Modeler',
        description: 'Enterprise architecture modeling with AI assistance and team collaboration',
        category: 'flagship',
        tags: [
          { label: 'Interactive Diagrams', highlight: true },
          { label: 'Database-Backed', highlight: true },
        ],
        badge: 'FLAGSHIP',
        relevantTopics: ['enterprise architecture', 'system design', 'architecture documentation', 'stakeholder communication', 'ArchiMate', 'architecture diagrams'],
      },
      {
        href: '/tools/wardley-map-builder',
        title: 'Wardley Map Builder',
        description: 'Strategic value chain mapping for technology decisions and evolution',
        category: 'flagship',
        tags: [
          { label: 'Interactive Canvas', highlight: true },
          { label: 'Export', highlight: true },
        ],
        badge: 'FLAGSHIP',
        relevantTopics: ['strategy', 'value chain', 'technology evolution', 'strategic planning', 'wardley maps', 'build vs buy'],
      },
      {
        href: '/tools/technology-due-diligence',
        title: 'Technology Due Diligence',
        description: 'Guided 5-part assessment combining codebase health, architecture maturity, security, vendor risk, and tech debt into a comprehensive report',
        category: 'flagship',
        tags: [
          { label: 'Multi-Tool Workflow', highlight: true },
          { label: 'PDF Report', highlight: true },
        ],
        badge: 'PREMIUM',
        relevantTopics: ['due diligence', 'acquisition', 'investment', 'audit', 'technology assessment', 'comprehensive review', 'vendor evaluation'],
      },
      {
        href: '/tools/foundry',
        title: 'Foundry',
        description: 'AI-powered business plan generator: idea to lean canvas to comprehensive plan',
        category: 'flagship',
        tags: [
          { label: 'AI-Powered', highlight: true },
          { label: 'Database-Backed', highlight: true },
        ],
        badge: 'NEW',
        relevantTopics: ['business plan', 'lean canvas', 'startup planning', 'business model', 'strategic planning', 'AI generation'],
      },
    ],
  },
  {
    id: 'compliance',
    title: 'Compliance & Risk',
    icon: '⚖️',
    description: 'Comprehensive compliance assessments for GDPR, SOC 2, HIPAA, ISO 27001, and more.',
    tools: [
      {
        href: '/tools/gdpr-compliance',
        title: 'GDPR Compliance',
        description: 'Complete GDPR readiness assessment across all key requirements',
        category: 'compliance',
        tags: [{ label: '📋 Assessment' }, { label: 'Free' }],
        badge: 'NEW',
        relevantTopics: ['GDPR', 'privacy', 'data protection', 'EU compliance', 'personal data'],
      },
      {
        href: '/tools/soc2-readiness',
        title: 'SOC 2 Readiness',
        description: 'Trust Service Criteria assessment for SOC 2 Type I/II certification',
        category: 'compliance',
        tags: [{ label: '📋 Assessment' }, { label: 'Free' }],
        badge: 'NEW',
        relevantTopics: ['SOC 2', 'audit', 'security certification', 'trust services', 'compliance'],
      },
      {
        href: '/tools/hipaa-compliance',
        title: 'HIPAA Compliance',
        description: 'Administrative, physical, and technical safeguards assessment',
        category: 'compliance',
        tags: [{ label: '📋 Assessment' }, { label: 'Free' }],
        badge: 'NEW',
        relevantTopics: ['HIPAA', 'healthcare', 'PHI', 'health data', 'medical compliance'],
      },
      {
        href: '/tools/iso27001-gap-analysis',
        title: 'ISO 27001 Gap Analysis',
        description: 'Annex A controls assessment for ISO 27001:2022 certification',
        category: 'compliance',
        tags: [{ label: '📋 Assessment' }, { label: 'Free' }],
        badge: 'NEW',
        relevantTopics: ['ISO 27001', 'information security', 'ISMS', 'security controls'],
      },
      {
        href: '/tools/nist-csf-assessment',
        title: 'NIST CSF Assessment',
        description: 'Cybersecurity Framework 2.0 assessment across all core functions',
        category: 'compliance',
        tags: [{ label: '📋 Assessment' }, { label: 'Free' }],
        badge: 'NEW',
        relevantTopics: ['NIST', 'cybersecurity framework', 'security assessment', 'cyber risk'],
      },
      {
        href: '/tools/pci-dss-checker',
        title: 'PCI DSS Checker',
        description: 'Payment card industry compliance across all 12 requirements',
        category: 'compliance',
        tags: [{ label: '📋 Assessment' }, { label: 'Free' }],
        badge: 'NEW',
        relevantTopics: ['PCI DSS', 'payment security', 'credit cards', 'payment processing'],
      },
      {
        href: '/tools/dora-compliance',
        title: 'DORA Compliance',
        description: 'Digital Operational Resilience Act assessment for EU financial services',
        category: 'compliance',
        tags: [{ label: '📋 Assessment' }, { label: 'Free' }],
        badge: 'NEW',
        relevantTopics: ['DORA', 'financial services', 'operational resilience', 'EU regulation'],
      },
      {
        href: '/tools/stamp-framework',
        title: 'STAMP Operational Resilience',
        description: 'Operational resilience assessment across Services, Tolerances, Architecture, Monitoring, and Proof — aligned to FCA and DORA',
        category: 'compliance',
        tags: [{ label: '📋 Assessment' }, { label: 'Free' }],
        badge: 'NEW',
        relevantTopics: ['operational resilience', 'STAMP framework', 'FCA', 'DORA', 'impact tolerances', 'important business services', 'resilience testing', 'third-party risk', 'service mapping'],
      },
      {
        href: '/tools/vendor-risk-assessment',
        title: 'Vendor Risk Assessment',
        description: 'Comprehensive third-party vendor security and compliance evaluation',
        category: 'compliance',
        tags: [{ label: '📋 Assessment' }, { label: 'Free' }],
        relevantTopics: ['vendor management', 'third-party risk', 'supply chain security', 'vendor evaluation'],
      },
      {
        href: '/tools/security-assessment-checklist',
        title: 'Security Assessment Checklist',
        description: 'Comprehensive security posture evaluation across all domains',
        category: 'compliance',
        tags: [{ label: '📋 Assessment' }, { label: 'Free' }],
        relevantTopics: ['security assessment', 'security posture', 'security audit', 'vulnerability assessment'],
      },
      {
        href: '/tools/ai-ethics-assessment',
        title: 'AI Ethics Assessment',
        description: 'Evaluate AI systems for bias, fairness, transparency, and ethical considerations',
        category: 'compliance',
        tags: [{ label: '📋 Assessment' }, { label: 'Free' }],
        relevantTopics: ['AI ethics', 'ML fairness', 'AI governance', 'responsible AI', 'AI bias'],
      },
      {
        href: '/tools/nis2-readiness',
        title: 'NIS2 Readiness',
        description: 'EU critical infrastructure cybersecurity directive readiness assessment',
        category: 'compliance',
        tags: [{ label: '📋 Assessment' }, { label: 'Free' }],
        badge: 'NEW',
        relevantTopics: ['NIS2', 'EU directive', 'critical infrastructure', 'cybersecurity', 'network security'],
      },
      {
        href: '/tools/ccpa-compliance',
        title: 'CCPA/CPRA Compliance',
        description: 'California privacy law compliance for consumer rights and data handling',
        category: 'compliance',
        tags: [{ label: '📋 Assessment' }, { label: 'Free' }],
        badge: 'NEW',
        relevantTopics: ['CCPA', 'CPRA', 'California privacy', 'consumer rights', 'data privacy', 'US privacy law'],
      },
      {
        href: '/tools/eu-ai-act-compliance',
        title: 'EU AI Act Compliance',
        description: 'Assess AI system risk levels under EU AI Act with compliance checklists',
        category: 'compliance',
        tags: [{ label: '📋 Assessment' }, { label: 'Free' }],
        relevantTopics: ['EU AI Act', 'AI regulation', 'AI risk levels', 'AI compliance', 'EU regulation'],
      },
      {
        href: '/tools/wcag-compliance',
        title: 'WCAG 2.2 Compliance',
        description: 'Web accessibility assessment for A, AA, and AAA conformance',
        category: 'compliance',
        tags: [{ label: '📋 Assessment' }, { label: 'Free' }],
        badge: 'NEW',
        relevantTopics: ['WCAG', 'accessibility', 'a11y', 'web accessibility', 'ADA compliance', 'inclusive design'],
      },
      {
        href: '/tools/license-compliance-checker',
        title: 'License Compliance Checker',
        description: 'Check open source licenses, detect GPL conflicts, analyze commercial compatibility',
        category: 'compliance',
        tags: [{ label: '📊 Excel Export' }, { label: 'Free' }],
        relevantTopics: ['open source licenses', 'GPL', 'MIT', 'license compliance', 'software licensing', 'OSS'],
      },
      {
        href: '/tools/tech-stack-risk-radar',
        title: 'Tech Stack Risk Radar',
        description: 'Assess technology adoption risks and create mitigation strategies',
        category: 'compliance',
        tags: [{ label: '📋 Assessment' }, { label: 'Free' }],
        relevantTopics: ['technology risk', 'tech stack assessment', 'risk mitigation', 'technology adoption'],
      },
    ],
  },
  {
    id: 'architecture',
    title: 'Architecture & Design',
    icon: '🏗️',
    description: 'Tools for system architecture, design decisions, and technical planning.',
    tools: [
      {
        href: '/tools/build-vs-buy-matrix',
        title: 'Build vs Buy Matrix',
        description: 'Framework for make-or-buy decisions with weighted criteria analysis',
        category: 'architecture',
        tags: [{ label: '📊 Calculator' }, { label: 'Free' }],
        relevantTopics: ['build vs buy', 'make or buy', 'vendor selection', 'custom development', 'third-party solutions'],
      },
      {
        href: '/tools/architecture-maturity-assessment',
        title: 'Architecture Maturity Assessment',
        description: 'Evaluate your architecture practices against industry standards',
        category: 'architecture',
        tags: [{ label: '📋 Assessment' }, { label: 'Free' }],
        relevantTopics: ['architecture maturity', 'architecture governance', 'maturity models', 'architecture practices'],
      },
      {
        href: '/tools/architecture-calculator',
        title: 'Architecture Calculator',
        description: 'Estimate system capacity, scaling requirements, and resource needs',
        category: 'architecture',
        tags: [{ label: '📊 Calculator' }, { label: 'Free' }],
        relevantTopics: ['capacity planning', 'system scaling', 'resource estimation', 'architecture sizing'],
      },
      {
        href: '/tools/microservices-dependency-mapper',
        title: 'Microservices Dependency Mapper',
        description: 'Visualize and analyze service dependencies and communication patterns',
        category: 'architecture',
        tags: [{ label: '📊 Visualization' }, { label: 'Free' }],
        relevantTopics: ['microservices', 'service dependencies', 'distributed systems', 'service mesh'],
      },
      {
        href: '/tools/database-migration-risk-assessor',
        title: 'Database Migration Risk Assessor',
        description: 'Evaluate risks and plan database migration strategies',
        category: 'architecture',
        tags: [{ label: '📋 Assessment' }, { label: 'Free' }],
        relevantTopics: ['database migration', 'data migration', 'database upgrade', 'migration planning'],
      },
      {
        href: '/tools/codebase-health-analyzer',
        title: 'Codebase Health Analyzer',
        description: 'Assess technical debt, code quality, and maintainability',
        category: 'architecture',
        tags: [{ label: '📋 Assessment' }, { label: 'Free' }],
        relevantTopics: ['code quality', 'technical debt', 'maintainability', 'code health', 'refactoring'],
      },
      {
        href: '/tools/tech-debt-prioritizer',
        title: 'Tech Debt Prioritizer',
        description: 'Prioritize technical debt repayment based on impact and effort',
        category: 'architecture',
        tags: [{ label: '📊 Calculator' }, { label: 'Free' }],
        relevantTopics: ['tech debt', 'technical debt prioritization', 'refactoring', 'debt paydown'],
      },
      {
        href: '/tools/api-contract',
        title: 'API Contract Designer',
        description: 'Design and document APIs with visual endpoint builder, OpenAPI spec generation, and breaking change detection',
        category: 'architecture',
        tags: [{ label: '📄 API Design', highlight: true }, { label: '⭐ Premium' }],
        badge: 'PREMIUM',
        relevantTopics: ['API design', 'OpenAPI', 'REST API', 'API documentation', 'API contracts', 'breaking changes'],
      },
      {
        href: '/tools/system-design',
        title: 'System Design Canvas',
        description: 'Architecture diagramming with drag-and-drop components, connections, cost estimation, and export to PNG/SVG',
        category: 'architecture',
        tags: [{ label: '🎨 Visual Designer', highlight: true }, { label: '⭐ Premium' }],
        badge: 'PREMIUM',
        relevantTopics: ['system design', 'architecture diagrams', 'infrastructure design', 'system architecture', 'visual design'],
      },
    ],
  },
  {
    id: 'cloud',
    title: 'Cloud & Infrastructure',
    icon: '☁️',
    description: 'Tools for cloud cost optimization, infrastructure planning, and DevOps.',
    tools: [
      {
        href: '/tools/cloud-cost-estimator',
        title: 'Cloud Cost Estimator',
        description: 'Estimate cloud infrastructure costs across AWS, Azure, and GCP',
        category: 'cloud',
        tags: [{ label: '📊 Calculator' }, { label: 'Free' }],
        relevantTopics: ['cloud costs', 'FinOps', 'cloud migration', 'infrastructure costs', 'cloud budgeting'],
      },
      {
        href: '/tools/kubernetes-cost-optimizer',
        title: 'Kubernetes Cost Optimizer',
        description: 'Analyze and optimize Kubernetes cluster costs and resource allocation',
        category: 'cloud',
        tags: [{ label: '📊 Calculator' }, { label: 'Free' }],
        relevantTopics: ['Kubernetes', 'K8s costs', 'container optimization', 'cluster sizing'],
      },
      {
        href: '/tools/cicd-maturity-grader',
        title: 'CI/CD Maturity Grader',
        description: 'Assess your continuous integration and deployment practices',
        category: 'cloud',
        tags: [{ label: '📋 Assessment' }, { label: 'Free' }],
        relevantTopics: ['CI/CD', 'DevOps maturity', 'deployment automation', 'continuous delivery'],
      },
      {
        href: '/tools/business-continuity',
        title: 'Business Continuity Planner',
        description: 'Create and assess disaster recovery and business continuity plans',
        category: 'cloud',
        tags: [{ label: '📋 Assessment' }, { label: 'Free' }],
        relevantTopics: ['disaster recovery', 'business continuity', 'DR planning', 'resilience'],
      },
      {
        href: '/tools/connectivity-tester',
        title: 'Connectivity Tester',
        description: 'Browser-based network diagnostics: latency, packet loss, and bandwidth testing',
        category: 'cloud',
        tags: [{ label: '📡 Diagnostics' }, { label: 'Free' }],
        relevantTopics: ['network diagnostics', 'latency', 'bandwidth', 'connectivity', 'network testing'],
      },
      {
        href: '/outages',
        title: 'Cloud Outage Tracker',
        description: 'Track major cloud outages from 2020 to present with RCA analysis',
        category: 'cloud',
        tags: [{ label: '🔴 Live Data' }, { label: 'Free' }],
        relevantTopics: ['cloud outages', 'AWS outage', 'Azure outage', 'GCP outage', 'incident history', 'downtime'],
      },
      {
        href: '/tools/api-rate-limit-planner',
        title: 'API Rate Limit Planner',
        description: 'Design rate limiting strategies for APIs with quota calculations',
        category: 'cloud',
        tags: [{ label: '🔧 Planner' }, { label: 'Free' }],
        relevantTopics: ['rate limiting', 'API throttling', 'API quotas', 'API gateway', 'traffic management'],
      },
    ],
  },
  {
    id: 'team',
    title: 'Team & Organization',
    icon: '👥',
    description: 'Tools for team scaling, organizational design, and people management.',
    tools: [
      {
        href: '/tools/engineering-org-design',
        title: 'Engineering Org Designer',
        description: 'Model team structures, reporting lines, and organizational patterns',
        category: 'team',
        tags: [{ label: '📊 Visualization' }, { label: 'Free' }],
        relevantTopics: ['org design', 'team structure', 'reporting lines', 'organizational design', 'team topology'],
      },
      {
        href: '/tools/team-scaling-calculator',
        title: 'Team Scaling Calculator',
        description: 'Plan engineering team growth based on product and business needs',
        category: 'team',
        tags: [{ label: '📊 Calculator' }, { label: 'Free' }],
        relevantTopics: ['team scaling', 'hiring plan', 'headcount', 'team growth', 'engineering capacity'],
      },
      {
        href: '/tools/headcount-forecasting',
        title: 'Headcount Forecasting',
        description: 'Project future staffing needs based on growth and attrition',
        category: 'team',
        tags: [{ label: '📊 Calculator' }, { label: 'Free' }],
        relevantTopics: ['headcount planning', 'workforce planning', 'hiring forecast', 'staffing'],
      },
      {
        href: '/tools/salary-band-calculator',
        title: 'Salary Band Calculator',
        description: 'Create competitive compensation bands for engineering roles',
        category: 'team',
        tags: [{ label: '📊 Calculator' }, { label: 'Free' }],
        relevantTopics: ['compensation', 'salary bands', 'pay equity', 'engineering salaries'],
      },
      {
        href: '/tools/cto-compensation-benchmark',
        title: 'CTO Compensation Benchmark',
        description: 'Research-backed salary and total compensation benchmarks for CTOs and tech executives by company stage, size, and geography',
        category: 'team',
        tags: [{ label: '📊 Benchmark' }, { label: 'Free' }],
        badge: 'NEW',
        relevantTopics: ['CTO salary', 'VP engineering compensation', 'executive pay', 'startup CTO pay', 'tech executive compensation', 'equity benchmarks'],
      },
      {
        href: '/tools/interview-question-bank',
        title: 'Interview Question Bank',
        description: 'Curated interview questions for engineering roles by level and focus',
        category: 'team',
        tags: [{ label: '📚 Reference' }, { label: 'Free' }],
        relevantTopics: ['interviewing', 'hiring', 'interview questions', 'technical interviews'],
      },
      {
        href: '/tools/oncall-rotation-optimizer',
        title: 'On-Call Rotation Optimizer',
        description: 'Design fair and sustainable on-call schedules for your team',
        category: 'team',
        tags: [{ label: '📊 Calculator' }, { label: 'Free' }],
        relevantTopics: ['on-call', 'incident response', 'SRE', 'team scheduling', 'burnout prevention'],
      },
      {
        href: '/tools/engineering-ladder',
        title: 'Engineering Ladder Builder',
        description: 'Design career frameworks with IC and management tracks, competency matrices, and progression criteria',
        category: 'team',
        tags: [{ label: '📊 Framework Builder', highlight: true }, { label: '⭐ Premium' }],
        badge: 'PREMIUM',
        relevantTopics: ['career ladder', 'engineering levels', 'career framework', 'IC track', 'management track', 'competency matrix'],
      },
      {
        href: '/tools/team-topology',
        title: 'Team Topology Designer',
        description: 'Design team structures based on Team Topologies with stream-aligned, platform, enabling, and complicated-subsystem teams',
        category: 'team',
        tags: [{ label: '🎨 Visual Designer', highlight: true }, { label: '⭐ Premium' }],
        badge: 'PREMIUM',
        relevantTopics: ['team topologies', 'stream-aligned teams', 'platform teams', 'team design', 'organizational design'],
      },
      {
        href: '/tools/one-on-one',
        title: '1:1 Meeting Assistant',
        description: 'Run effective 1:1s with structured templates, AI-suggested talking points, and action item tracking',
        category: 'team',
        tags: [{ label: '📋 Meeting Templates', highlight: true }, { label: '⭐ Premium' }],
        badge: 'PREMIUM',
        relevantTopics: ['one on one', '1:1 meetings', 'manager meetings', 'coaching', 'feedback', 'team development'],
      },
    ],
  },
  {
    id: 'operations',
    title: 'Operations & SRE',
    icon: '🔧',
    description: 'Tools for incident management, SRE practices, and operational excellence.',
    tools: [
      {
        href: '/tools/incident-postmortem',
        title: 'Incident Postmortem',
        description: 'Structure blameless postmortems with action items and learnings',
        category: 'operations',
        tags: [{ label: '📋 Template' }, { label: 'Free' }],
        relevantTopics: ['postmortem', 'incident review', 'blameless culture', 'learning from failures', 'SRE'],
      },
      {
        href: '/tools/incident-response',
        title: 'Incident Response Planner',
        description: 'Create and maintain incident response runbooks and procedures',
        category: 'operations',
        tags: [{ label: '📋 Template' }, { label: 'Free' }],
        relevantTopics: ['incident response', 'runbooks', 'incident management', 'on-call'],
      },
      {
        href: '/tools/incident-severity-classifier',
        title: 'Incident Severity Classifier',
        description: 'Consistently classify incident severity based on impact criteria',
        category: 'operations',
        tags: [{ label: '📊 Calculator' }, { label: 'Free' }],
        relevantTopics: ['incident severity', 'SEV levels', 'incident classification', 'impact assessment'],
      },
      {
        href: '/tools/sla-generator',
        title: 'SLA Generator',
        description: 'Create service level agreements with appropriate targets and terms',
        category: 'operations',
        tags: [{ label: '📋 Template' }, { label: 'Free' }],
        relevantTopics: ['SLA', 'SLO', 'service level agreements', 'uptime commitments'],
      },
      {
        href: '/tools/engineering-metrics-dashboard',
        title: 'Engineering Metrics Dashboard',
        description: 'Track DORA metrics and engineering team performance',
        category: 'operations',
        tags: [{ label: '📊 Dashboard' }, { label: 'Free' }],
        relevantTopics: ['DORA metrics', 'engineering productivity', 'deployment frequency', 'lead time', 'MTTR'],
      },
    ],
  },
  {
    id: 'planning',
    title: 'Planning & Finance',
    icon: '📊',
    description: 'Tools for budgeting, ROI calculations, and strategic planning.',
    tools: [
      {
        href: '/tools/engineering-roi-calculator',
        title: 'Engineering ROI Calculator',
        description: 'Calculate return on investment for engineering initiatives',
        category: 'planning',
        tags: [{ label: '📊 Calculator' }, { label: 'Free' }],
        relevantTopics: ['ROI', 'engineering investment', 'cost-benefit analysis', 'project justification'],
      },
      {
        href: '/tools/rfp-generator',
        title: 'RFP Generator',
        description: 'Create comprehensive RFPs for technology vendor selection',
        category: 'planning',
        tags: [{ label: '📋 Template' }, { label: 'Free' }],
        relevantTopics: ['RFP', 'vendor selection', 'procurement', 'technology purchasing'],
      },
      {
        href: '/tools/architecture-scenario-planner',
        title: 'Architecture Scenario Planner',
        description: 'Model different architecture scenarios and compare trade-offs',
        category: 'planning',
        tags: [{ label: '📊 Calculator' }, { label: 'Free' }],
        relevantTopics: ['architecture planning', 'scenario analysis', 'trade-offs', 'technology roadmap'],
      },
      {
        href: '/tools/cto-rating-framework',
        title: 'CTO Rating Framework',
        description: 'Self-assessment tool for CTO skills and development areas',
        category: 'planning',
        tags: [{ label: '📋 Assessment' }, { label: 'Free' }],
        relevantTopics: ['CTO skills', 'leadership assessment', 'career development', 'self-assessment'],
      },
      {
        href: '/tools/sprint-capacity-planner',
        title: 'Sprint Capacity Planner',
        description: 'Plan sprint capacity with team availability and velocity tracking',
        category: 'planning',
        tags: [{ label: '📊 Planner' }, { label: 'Free' }],
        relevantTopics: ['sprint planning', 'capacity planning', 'velocity', 'agile', 'scrum', 'team capacity'],
      },
    ],
  },
  {
    id: 'techstack',
    title: 'Tech Stack & Code Quality',
    icon: '💻',
    description: 'Make informed technology decisions and manage technical debt.',
    tools: [
      {
        href: '/tools/tech-stack-decision',
        title: 'Tech Stack Decision',
        description: 'AI-powered tech recommendations with 200+ technologies and ADR generator',
        category: 'techstack',
        tags: [{ label: '🤖 AI-Powered' }, { label: 'Free' }],
        relevantTopics: ['tech stack', 'technology selection', 'framework comparison', 'technology decisions', 'ADR'],
      },
      {
        href: '/tools/tech-debt-paydown-simulator',
        title: 'Tech Debt Paydown Simulator',
        description: 'Simulate tech debt paydown strategies and visualize long-term impact',
        category: 'techstack',
        tags: [{ label: '📊 Simulator' }, { label: 'Free' }],
        relevantTopics: ['tech debt paydown', 'debt simulation', 'technical debt strategy', 'refactoring plan'],
      },
    ],
  },
];

/**
 * Get all tools as a flat array
 */
export function getAllTools(): Tool[] {
  return toolCategories.flatMap(category => category.tools);
}

/**
 * Get tools relevant to a given topic
 */
export function getToolsForTopic(topic: string): Tool[] {
  const lowerTopic = topic.toLowerCase();
  return getAllTools().filter(tool =>
    tool.relevantTopics?.some(t =>
      lowerTopic.includes(t.toLowerCase()) || t.toLowerCase().includes(lowerTopic)
    )
  );
}

/**
 * Get a summary of tools for AI prompt injection
 * Returns a formatted string suitable for including in AI prompts
 */
export function getToolsSummaryForAI(): string {
  const tools = getAllTools().filter(t => t.relevantTopics && t.relevantTopics.length > 0);

  return tools.map(tool =>
    `- **${tool.title}** (${tool.href}) - ${tool.description}. Relevant for: ${tool.relevantTopics?.join(', ')}.`
  ).join('\n');
}
